import {
  FOC_TRANSITION_TOUR_STEPS,
  type FocTransitionTourStepId,
  type FocTransitionTourSurface,
} from './focTransitionTourSteps';

/**
 * Hosts (IR page, Escalate button, Planning dashboard) register UI moves.
 */
export type FocTransitionTourApi = {
  getLocation?: () => { pathname: string; search: string };
  ensureNewIcs201?: () => void;
  openEscalateModal?: () => void;
  /** Escalate current workspace; returns Planning P href with tour=planning, or null. */
  escalateWorkspace?: () => Promise<string | null>;
  navigateTo?: (href: string) => void;
  ensurePlanningPhase0?: () => void;
  showPlanningAgenda?: () => void;
  selectPlanningWidget?: (widgetName: string) => void;
  scrollToTour?: (tour: string) => void;
};

let api: Partial<FocTransitionTourApi> = {};
let pendingStep: FocTransitionTourStepId | null = null;

export function registerFocTransitionTourApi(
  partial: Partial<FocTransitionTourApi>
): () => void {
  api = { ...api, ...partial };
  if (pendingStep) {
    const step = pendingStep;
    if (currentSurface() === surfaceFor(step)) {
      pendingStep = null;
      prepareFocTransitionStep(step);
    }
  }
  return () => {
    for (const key of Object.keys(partial) as (keyof FocTransitionTourApi)[]) {
      if (api[key] === partial[key]) {
        delete api[key];
      }
    }
  };
}

export function getFocTransitionTourApi(): Partial<FocTransitionTourApi> {
  return api;
}

function surfaceFor(id: FocTransitionTourStepId): FocTransitionTourSurface {
  return (
    FOC_TRANSITION_TOUR_STEPS.find((step) => step.id === id)?.surface ??
    'initialResponse'
  );
}

function readLocation(): { pathname: string; search: string } {
  const fromHost = getFocTransitionTourApi().getLocation?.();
  if (fromHost) return fromHost;
  if (typeof window !== 'undefined' && window.location) {
    return {
      pathname: window.location.pathname,
      search: window.location.search,
    };
  }
  return { pathname: '/hub', search: '?openTab=Incidents' };
}

function currentSurface(): FocTransitionTourSurface {
  const { pathname } = readLocation();
  if (pathname.includes('/dashboard')) return 'planning';
  if (
    pathname.includes('/initial-response') ||
    pathname.includes('/iap/')
  ) {
    return 'initialResponse';
  }
  return 'initialResponse';
}

function ensureSurface(id: FocTransitionTourStepId): boolean {
  const needed = surfaceFor(id);
  const current = currentSurface();
  if (needed === current) {
    pendingStep = null;
    return true;
  }
  // Planning steps while still on IR wait for escalate navigation.
  pendingStep = id;
  return false;
}

export function prepareFocTransitionStep(id: FocTransitionTourStepId) {
  if (!ensureSurface(id)) return;

  const host = getFocTransitionTourApi();
  const def = FOC_TRANSITION_TOUR_STEPS.find((step) => step.id === id);
  const surface = def?.surface ?? 'initialResponse';

  if (surface === 'initialResponse') {
    host.ensureNewIcs201?.();
    if (id === 'trn001a') {
      host.openEscalateModal?.();
    }
  } else {
    host.ensurePlanningPhase0?.();
    if (def?.widget) {
      host.selectPlanningWidget?.(def.widget);
    }
    if (
      id === 'trn003' ||
      id === 'trn005a' ||
      def?.tour === 'planning-agenda' ||
      def?.tour === 'planning-show-agenda'
    ) {
      host.showPlanningAgenda?.();
    }
  }

  if (def?.tour) {
    host.scrollToTour?.(def.tour);
  }
}

export function resetFocTransitionTourBridgeForTests() {
  api = {};
  pendingStep = null;
}
